<script setup>
import { computed, ref, watch } from "vue";
import { CircleCheckFilled, CreditCard, Promotion, WarningFilled } from "@element-plus/icons-vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  reference: {
    type: String,
    required: true,
  },
  action: {
    type: String,
    default: "prepare-direction-approved",
  },
  title: {
    type: String,
    default: "",
  },
  intro: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "",
  },
  requirements: {
    type: Array,
    default: () => [],
  },
  commentLabel: {
    type: String,
    default: "",
  },
  commentPlaceholder: {
    type: String,
    default: "",
  },
  confirmLabel: {
    type: String,
    default: "",
  },
  notice: {
    type: String,
    default: "",
  },
  saving: { type: Boolean, default: false },
  blocked: {
    type: Boolean,
    default: false,
  },
  blockedMessage: {
    type: String,
    default: "Hay requisitos pendientes de resolver antes de continuar.",
  },
});

const emit = defineEmits(["update:modelValue", "confirm"]);
const comment = ref("");

const actionDefinitions = {
  "prepare-direction-approved": {
    icon: Promotion,
    title: "Enviar opinión a Dirección · Aprobar",
    intro: (reference) => `La solicitud ${reference} se enviará a Dirección con la opinión de A51: aprobar. La decisión final es de Dirección.`,
    description: "Esta acción notifica a Dirección que A51 recomienda aprobar esta solicitud (no vinculante) — Dirección tomará la decisión definitiva.",
    requirements: ["Importe sugerido guardado", "Documentación inicial validada"],
    commentLabel: "Comentario visible a Dirección",
    commentPlaceholder: "Escribe un comentario para Dirección...",
    confirmLabel: "CONFIRMAR ENVÍO",
    notice: "Dirección podrá revisar la solicitud y continuar su tramitación.",
  },
  "prepare-direction-rejected": {
    icon: Promotion,
    title: "Enviar opinión a Dirección · Denegar",
    intro: (reference) => `La solicitud ${reference} se enviará a Dirección con la opinión de A51: denegar. La decisión final es de Dirección.`,
    description: "Esta acción notifica a Dirección que A51 recomienda denegar esta solicitud (no vinculante) — Dirección tomará la decisión definitiva.",
    requirements: ["Importe sugerido guardado", "Documentación inicial validada"],
    commentLabel: "Comentario visible a Dirección",
    commentPlaceholder: "Escribe un comentario para Dirección...",
    confirmLabel: "CONFIRMAR ENVÍO",
    notice: "Dirección podrá revisar la solicitud y continuar su tramitación.",
  },
  "prepare-direction-undecided": {
    icon: Promotion,
    title: "Enviar opinión a Dirección · En duda",
    intro: (reference) => `La solicitud ${reference} se enviará a Dirección sin una opinión clara de A51. La decisión final es de Dirección.`,
    description: "Esta acción notifica a Dirección que A51 no tiene una recomendación clara sobre esta solicitud — Dirección tomará la decisión definitiva.",
    requirements: ["Importe sugerido guardado", "Documentación inicial validada"],
    commentLabel: "Comentario visible a Dirección",
    commentPlaceholder: "Escribe un comentario para Dirección...",
    confirmLabel: "CONFIRMAR ENVÍO",
    notice: "Dirección podrá revisar la solicitud y continuar su tramitación.",
  },
  "approve-payment": {
    icon: CreditCard,
    title: "Aprobar para pago",
    intro: (reference) => `La solicitud ${reference} será enviada al circuito de pago.`,
    description: "Esta acción confirma que la ayuda está lista para ser abonada.",
    requirements: ["Decisión de Dirección aprobada", "Convenio firmado por ambas partes y documentación exigida validada"],
    commentLabel: "Comentario visible para Finanzas",
    commentPlaceholder: "Añade una indicación para el pago...",
    confirmLabel: "APROBAR PARA PAGO",
    notice: "Finanzas recibirá la solicitud con la documentación y el importe aprobados.",
  },
  "close-paid": {
    icon: CircleCheckFilled,
    title: "Cerrar expediente",
    intro: (reference) => `La solicitud ${reference} quedará cerrada.`,
    description: "Esta acción cierra un expediente ya pagado una vez revisado el justificante final de la acción.",
    requirements: ["Expediente pagado", "Justificante final validado"],
    commentLabel: "Comentario (queda en el historial)",
    commentPlaceholder: "Añade una nota sobre el cierre...",
    confirmLabel: "CERRAR EXPEDIENTE",
    notice: "El solicitante recibirá un correo indicando que su expediente está cerrado.",
  },
};

const definition = computed(() => actionDefinitions[props.action] || actionDefinitions["prepare-direction-approved"]);
const content = computed(() => ({
  ...definition.value,
  title: props.title || definition.value.title,
  intro: props.intro || definition.value.intro(props.reference),
  description: props.description || definition.value.description,
  requirements: props.requirements.length ? props.requirements : definition.value.requirements,
  commentLabel: props.commentLabel || definition.value.commentLabel,
  commentPlaceholder: props.commentPlaceholder || definition.value.commentPlaceholder,
  confirmLabel: props.confirmLabel || definition.value.confirmLabel,
  notice: props.notice || definition.value.notice,
}));

const visible = computed({
  get: () => props.modelValue,
  set: (value) => { if (!props.saving) emit("update:modelValue", value); },
});

watch(() => props.modelValue, value => { if (!value) comment.value = ""; });
const close = () => {
  if (props.saving) return;
  comment.value = "";
  visible.value = false;
};

const confirm = () => {
  if (props.blocked || props.saving) return;
  emit("confirm", { action: props.action, comment: comment.value.trim() });
};
</script>

<template>
  <el-dialog v-model="visible" width="620px" :show-close="false" :lock-scroll="true" :close-on-click-modal="!saving" :close-on-press-escape="!saving" class="social-fund-action-dialog">
    <section v-if="visible" class="social-fund-action-dialog__content">
      <img class="social-fund-action-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png"
        alt="Cerrar" @click="close" />

      <div class="social-fund-action-dialog__hero">
        <span><el-icon>
            <component :is="content.icon" />
          </el-icon></span>
        <h2>{{ content.title }}</h2>
      </div>

      <p class="social-fund-action-dialog__intro">{{ content.intro }}</p>
      <p class="social-fund-action-dialog__description">{{ content.description }}</p>

      <p v-if="blocked" class="social-fund-action-dialog__blocked">
        <el-icon>
          <WarningFilled />
        </el-icon>{{ blockedMessage }}
      </p>
      <div v-else-if="content.requirements.length" class="social-fund-action-dialog__requirements">
        <p v-for="requirement in content.requirements" :key="requirement"><el-icon>
            <CircleCheckFilled />
          </el-icon>{{ requirement }}</p>
      </div>

      <div class="form-item social-fund-action-dialog__field">
        <label for="social-fund-action-comment">{{ content.commentLabel }} <span>(opcional)</span></label>
        <el-input id="social-fund-action-comment" v-model="comment" :disabled="saving" type="textarea" :rows="3" maxlength="300"
          :placeholder="content.commentPlaceholder" />
        <p class="text-end small mb-0">{{ comment.length }} / 300</p>
      </div>

      <p v-if="content.notice" class="social-fund-action-dialog__notice">
        <el-icon>
          <CircleCheckFilled />
        </el-icon>{{ content.notice }}
      </p>

      <footer class="social-fund-action-dialog__actions">
        <button type="button" class="btn btn_yellow" :disabled="saving" @click="close">CANCELAR</button>
        <button type="button" class="btn btn_red" :disabled="blocked || saving" @click="confirm">{{ content.confirmLabel
        }}</button>
      </footer>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
:global(.el-dialog.social-fund-action-dialog) {
  border-radius: 20px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.social-fund-action-dialog :deep(.el-dialog__header) {
  display: none;
}

.social-fund-action-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.social-fund-action-dialog__content {
  color: #263052;
  padding: 8px 16px 14px;
  position: relative;
}

.social-fund-action-dialog__close {
  height: 38px;
  object-fit: contain;
  position: absolute;
  right: 12px;
  top: 8px;
  width: 38px;
}

.social-fund-action-dialog__hero {
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;
}

.social-fund-action-dialog__hero>span {
  align-items: center;
  background: #fff9e8;
  border: 2px solid #f7d20d;
  border-radius: 50%;
  color: #202845;
  display: inline-flex;
  font-size: 34px;
  height: 72px;
  justify-content: center;
  width: 72px;
}

.social-fund-action-dialog__hero h2 {
  color: #202845;
  font-size: 24px;
  font-weight: 900;
  margin: 13px 0 0;
}

.social-fund-action-dialog__hero h2::after {
  background: #f7d20d;
  content: "";
  display: block;
  height: 3px;
  margin: 12px auto 0;
  width: 58px;
}

.social-fund-action-dialog__intro,
.social-fund-action-dialog__description {
  font-size: 15px;
  line-height: 1.4;
  margin: 17px auto 0;
  max-width: 440px;
  text-align: center;
}

.social-fund-action-dialog__description {
  color: #69718a;
  margin-top: 7px;
}

.social-fund-action-dialog__requirements {
  border: 1px solid #dce0e9;
  border-radius: 10px;
  margin: 18px 0;
  padding: 11px 16px;
}

.social-fund-action-dialog__blocked {
  align-items: center;
  background: #fdf1f1;
  border: 1px solid #f3c1c1;
  border-radius: 10px;
  color: #b42318;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 9px;
  margin: 18px 0;
  padding: 11px 16px;
}

.social-fund-action-dialog__blocked .el-icon {
  color: #b42318;
  flex: 0 0 auto;
  font-size: 18px;
}

.social-fund-action-dialog__actions .btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.social-fund-action-dialog__requirements p {
  align-items: center;
  display: flex;
  font-size: 14px;
  gap: 9px;
  margin: 7px 0;
}

.social-fund-action-dialog__requirements .el-icon {
  color: #36a256;
  font-size: 17px;
}

.social-fund-action-dialog__field {
  margin: 0;
}

.social-fund-action-dialog__field label {
  color: #283150;
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 7px;
}

.social-fund-action-dialog__field label span {
  color: #747d9f;
  font-weight: 500;
}

.social-fund-action-dialog__field :deep(.el-textarea__inner) {
  border-radius: 10px;
  min-height: 92px !important;
  padding: 12px 14px;
}


.social-fund-action-dialog__notice {
  align-items: flex-start;
  background: #f4f2ff;
  border-radius: 8px;
  color: #626a81;
  display: flex;
  font-size: 12px;
  gap: 9px;
  line-height: 1.4;
  margin: 17px 0 0;
  padding: 10px 12px;
}

.social-fund-action-dialog__notice .el-icon {
  color: $bg-blue;
  flex: 0 0 auto;
  font-size: 17px;
}

.social-fund-action-dialog__actions {
  border-top: 1px solid #e2e5ed;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin: 20px -16px -14px;
  padding: 17px 16px;
}

.social-fund-action-dialog__actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 42px;
  min-width: 178px;
}

.social-fund-action-dialog__actions .btn_yellow {
  color: $bg-blue !important;
}

.social-fund-action-dialog__actions .btn_red {
  color: #fff !important;
}

@media (max-width: 767px) {
  .social-fund-action-dialog__close {
    right: 8px;
    top: 7px;
  }

  .social-fund-action-dialog__hero>span {
    font-size: 28px;
    height: 62px;
    width: 62px;
  }

  .social-fund-action-dialog__hero h2 {
    font-size: 21px;
  }

  .social-fund-action-dialog__intro,
  .social-fund-action-dialog__description {
    font-size: 14px;
  }

  .social-fund-action-dialog__requirements {
    padding: 9px 12px;
  }

  .social-fund-action-dialog__actions {
    flex-direction: column-reverse;
    margin: 18px -16px -14px;
    padding: 14px 16px;
  }

  .social-fund-action-dialog__actions .btn {
    width: 100%;
  }
}
</style>
